import { useMemo, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, CornerDownLeft, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useCalendars, useCalMonth, useTimelines, useEvents, useLanguages, useWords, useQuotes } from "@/lib/queries";
import { errMsg } from "@/lib/api";
import { fmtT, tlFormat } from "@/lib/calFormat";
import { evDate } from "@/lib/eventDate";
import { ListPick, Empty } from "@/components/studio/widgets/Pickers";

const InsertBtn = ({ onClick, testid }) => (
  <button onClick={onClick} title="Insert into chapter at the cursor" className="shrink-0 rounded p-1 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F59E0B]" data-testid={testid}><CornerDownLeft className="h-3.5 w-3.5" /></button>
);

const SearchBox = ({ value, onChange, testid, placeholder }) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-[#A8A090]" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-8 pl-7 text-xs" data-testid={testid} />
  </div>
);

export const CalendarWidget = ({ uid, cfg, set }) => {
  const { data: cals = [] } = useCalendars(uid);
  const cal = cals.find((c) => c.id === cfg.id) || (cfg.id ? null : cals[0]);
  const [cur, setCur] = useState(null);
  const [day, setDay] = useState(null);
  useEffect(() => { if (cal) setCur({ year: cal.today.year, month: cal.today.month }); }, [cal?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { data, error } = useCalMonth(cal?.id, cur?.year, cur?.month);
  if (!cals.length) return <Empty testid="cal-widget-empty">No calendars yet.</Empty>;
  const n = cal?.month_names.length || 12;
  const step = (d) => { const m = cur.month + d; setCur({ year: cur.year + (m < 0 ? -1 : m >= n ? 1 : 0), month: (m + n) % n }); setDay(null); };
  const sel = data?.days.find((d) => d.day === day);
  return (
    <div className="space-y-2" data-testid="cal-widget">
      <ListPick items={cals} value={cal?.id} onChange={(id) => set({ id })} testid="cal-widget-pick" placeholder="Calendar" />
      {cur && (
        <div className="flex items-center justify-between">
          <button onClick={() => step(-1)} className="rounded p-1 hover:bg-[#1A202C]" data-testid="cal-widget-prev"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-display text-lg text-[#F3EBDD]" data-testid="cal-widget-title">{cal.month_names[cur.month]} {cur.year}</span>
          <button onClick={() => step(1)} className="rounded p-1 hover:bg-[#1A202C]" data-testid="cal-widget-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
      {error && <Empty testid="cal-widget-error">{errMsg(error)}</Empty>}
      {data && (
        <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${data.weekdays.length || 7}, minmax(0, 1fr))` }}>
          {data.weekdays.map((w) => <div key={w} className="truncate text-center text-[9px] uppercase text-[#A8A090]">{w.slice(0, 2)}</div>)}
          {Array.from({ length: data.first_weekday ?? 0 }).map((_, i) => <div key={`b${i}`} />)}
          {data.days.map((d) => {
            const count = d.events.length + d.timeline_events.length;
            const today = cal.today.year === data.year && cal.today.month === data.month && cal.today.day === d.day;
            return (
              <button key={d.d} onClick={() => setDay(d.day)} data-testid={`cal-widget-day-${d.day}`}
                className={`relative h-6 rounded text-[11px] transition-colors ${day === d.day ? "bg-[#D97706] text-[#0B0D10]" : today ? "border border-[#F59E0B] text-[#F59E0B]" : "text-[#E8DFD1] hover:bg-[#1A202C]"}`}>
                {d.day}{count > 0 && <span className="absolute bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-[#8B5CF6]" />}
              </button>
            );
          })}
        </div>
      )}
      {sel && (
        <ul className="space-y-1 border-t border-[#2D3442] pt-2 text-xs" data-testid="cal-widget-events">
          {[...sel.events, ...sel.timeline_events].map((e, i) => <li key={`${e.id}-${i}`} className="truncate" style={{ color: e.color || "#C4B5FD" }}>{e.title}</li>)}
          {!sel.events.length && !sel.timeline_events.length && <li className="text-[#A8A090]">Nothing on this day.</li>}
        </ul>
      )}
    </div>
  );
};

export const TimelineWidget = ({ uid, cfg, set }) => {
  const { data: tls = [] } = useTimelines(uid);
  const tl = tls.find((t) => t.id === cfg.id) || (cfg.id ? null : tls[0]);
  const { data: events = [] } = useEvents(tl?.id);
  const [open, setOpen] = useState(null);
  const fmt = tl ? tlFormat(tls, tl) : null;
  const sorted = useMemo(() => [...events].sort((a, b) => (a.timestamp_int ?? 0) - (b.timestamp_int ?? 0)), [events]);
  if (!tls.length) return <Empty testid="tl-widget-empty">No timelines yet.</Empty>;
  return (
    <div className="space-y-2" data-testid="tl-widget">
      <ListPick items={tls} value={tl?.id} onChange={(id) => set({ id })} testid="tl-widget-pick" placeholder="Timeline" />
      {!sorted.length && <Empty>No events on this timeline.</Empty>}
      <ol className="relative ml-1.5 border-l border-[#2D3442]">
        {sorted.map((e) => (
          <li key={e.id} className="relative pb-2 pl-3" data-testid={`tl-widget-event-${e.id}`}>
            <span className="absolute -left-[4.5px] top-1.5 h-2 w-2 rounded-full" style={{ background: e.color || "#F59E0B" }} />
            <button onClick={() => e.description && setOpen(open === e.id ? null : e.id)} className={`w-full text-left ${e.description ? "" : "cursor-default"}`}>
              <div className="font-mono-lf text-[10px] text-[#A8A090]">{fmt ? fmtT(fmt, e.timestamp_int, true) : evDate(e)}</div>
              <div className="text-xs text-[#F3EBDD]">{e.title}</div>
            </button>
            {open === e.id && e.description && <p className="mt-1 text-[11px] text-[#A8A090]">{e.description}</p>}
          </li>
        ))}
      </ol>
    </div>
  );
};

export const DictionaryWidget = ({ uid, cfg, set, onInsert }) => {
  const { data: langs = [] } = useLanguages(uid);
  const lang = langs.find((l) => l.id === cfg.id) || (cfg.id ? null : langs[0]);
  const { data: words = [] } = useWords(lang?.id);
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return words.filter((w) => !s || [w.word, w.meaning, w.notes].some((x) => (x || "").toLowerCase().includes(s))).sort((a, b) => a.word.localeCompare(b.word)).slice(0, 200);
  }, [words, q]);
  if (!langs.length) return <Empty testid="dict-widget-empty">No languages yet.</Empty>;
  return (
    <div className="space-y-2" data-testid="dict-widget">
      <ListPick items={langs} value={lang?.id} onChange={(id) => set({ id })} testid="dict-widget-pick" placeholder="Language" />
      <SearchBox value={q} onChange={setQ} testid="dict-widget-search" placeholder="Word or meaning…" />
      <ul className="divide-y divide-[#2D3442]">
        {shown.map((w) => (
          <li key={w.id} className="flex items-start gap-2 py-1.5" data-testid={`dict-widget-word-${w.id}`}>
            <div className="min-w-0 flex-1">
              <span className="font-display text-base text-[#F3EBDD]">{w.word}</span>
              {w.pronunciation && <span className="ml-1.5 text-[10px] text-[#A8A090]">/{w.pronunciation}/</span>}
              {w.part_of_speech && <span className="ml-1.5 text-[10px] italic text-[#A8A090]">{w.part_of_speech}</span>}
              <div className="text-[11px] text-[#E8DFD1]">{w.meaning}</div>
            </div>
            {onInsert && <InsertBtn onClick={() => onInsert(w.word)} testid={`dict-widget-insert-${w.id}`} />}
          </li>
        ))}
      </ul>
      {!shown.length && <Empty>No words match.</Empty>}
    </div>
  );
};

const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const quoteText = (q) => (q.lines.length > 1
  ? q.lines.map((l) => `<p>${esc(l.speaker_name ? `“${l.text}” — ${l.speaker_name}` : `“${l.text}”`)}</p>`).join("")
  : `“${q.lines[0]?.text || ""}”`);

export const QuotesWidget = ({ uid, onInsert }) => {
  const { data: quotes = [] } = useQuotes(uid);
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return quotes.filter((x) => !s || JSON.stringify([x.lines, x.tags, x.context]).toLowerCase().includes(s)).slice(0, 100);
  }, [quotes, q]);
  return (
    <div className="space-y-2" data-testid="quotes-widget">
      <SearchBox value={q} onChange={setQ} testid="quotes-widget-search" placeholder="Search quotes, speakers, tags…" />
      {shown.map((x) => (
        <div key={x.id} className="flex gap-2 rounded border border-[#2D3442] bg-[#0B0D10] p-2" data-testid={`quotes-widget-quote-${x.id}`}>
          <div className="min-w-0 flex-1 space-y-0.5">
            {x.lines.map((l, i) => (
              <p key={i} className="font-display text-sm leading-snug text-[#F3EBDD]">“{l.text}”{l.speaker_name && <span className="ml-1 font-sans text-[10px] text-[#F59E0B]">{l.speaker_name}</span>}</p>
            ))}
          </div>
          {onInsert && <InsertBtn onClick={() => onInsert(quoteText(x))} testid={`quotes-widget-insert-${x.id}`} />}
        </div>
      ))}
      {!shown.length && <Empty testid="quotes-widget-empty">{quotes.length ? "No quotes match." : "No quotes in the catalog yet."}</Empty>}
    </div>
  );
};
